import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { QRCodeSVG } from 'qrcode.react'
import { useAuthStore, type MfaChallenge } from '@/stores/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Loader2, Eye, EyeOff } from 'lucide-react'

interface LoginFormData {
  email: string
  password: string
}

// react-hook-form's own rules, not a zod schema: this page is the only eager
// route, so a schema library here would sit in the pre-login bundle to check
// two fields. The lazy routes that need zod still use it.
const EMAIL_RULES = {
  required: 'Please enter a valid email',
  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Please enter a valid email' },
} as const

const PASSWORD_RULES = { required: 'Password is required' } as const

const REMEMBERED_EMAIL_KEY = 'remembered-email'
// Legacy key that used to hold a base64 password — never write it again, always purge it.
const LEGACY_REMEMBERED_PASS_KEY = 'remembered-pass'

function safeStorage(op: () => void): void {
  try {
    op()
  } catch {
    // localStorage unavailable (private browsing) — remembering is best-effort
  }
}

function MfaStep({
  challenge,
  rememberMe,
  onDone,
  onBack,
}: {
  challenge: MfaChallenge
  rememberMe: boolean
  onDone: () => void
  onBack: () => void
}) {
  const completeMfaLogin = useAuthStore((s) => s.completeMfaLogin)
  const isLoading = useAuthStore((s) => s.isLoading)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [enrollment, setEnrollment] = useState<{ secret: string; otpauth_uri: string } | null>(null)

  // Setup flow: mint the TOTP secret to scan as soon as the step opens.
  useEffect(() => {
    if (challenge.kind !== 'setup') return
    fetch('/api/v1/auth/mfa/enroll/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mfa_token: challenge.mfaToken }),
    })
      .then(async (r) => {
        if (!r.ok) throw new Error('Could not start MFA setup. Sign in again.')
        setEnrollment(await r.json())
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not start MFA setup.'))
  }, [challenge])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      const path = challenge.kind === 'code'
        ? '/api/v1/auth/mfa/verify/'
        : '/api/v1/auth/mfa/enroll/confirm/'
      await completeMfaLogin(path, { mfa_token: challenge.mfaToken, code }, rememberMe)
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed')
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-linear-to-br from-teal-50 to-amber-50/30 p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <CardTitle className="text-xl md:text-2xl">
            {challenge.kind === 'code' ? 'Two-Factor Code' : 'Set Up Two-Factor Auth'}
          </CardTitle>
          <CardDescription>
            {challenge.kind === 'code'
              ? 'Enter the 6-digit code from your authenticator app'
              : 'Your organization requires MFA. Scan this QR code with an authenticator app, then enter the code it shows.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            {error && (
              <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            {challenge.kind === 'setup' && enrollment && (
              <div className="flex flex-col items-center gap-2">
                <div className="rounded-lg bg-white p-3 border">
                  <QRCodeSVG value={enrollment.otpauth_uri} size={168} />
                </div>
                <p className="text-xs text-muted-foreground break-all text-center">
                  Manual key: <span className="font-mono">{enrollment.secret}</span>
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="mfa-code">Authentication code</Label>
              <Input
                id="mfa-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                autoFocus
              />
            </div>

            <Button type="submit" className="w-full" disabled={isLoading || code.length !== 6}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify'
              )}
            </Button>

            <button
              type="button"
              onClick={onBack}
              className="w-full text-sm text-muted-foreground hover:text-foreground"
            >
              Back to sign in
            </button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

export function LoginPage() {
  const navigate = useNavigate()
  const login = useAuthStore((s) => s.login)
  const isLoading = useAuthStore((s) => s.isLoading)
  const [error, setError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)

  const [savedEmail] = useState<string>(() => {
    try {
      // Purge the legacy stored password for anyone who had "Remember Me" on
      localStorage.removeItem(LEGACY_REMEMBERED_PASS_KEY)
      return localStorage.getItem(REMEMBERED_EMAIL_KEY) ?? ''
    } catch {
      return ''
    }
  })
  const [rememberMe, setRememberMe] = useState(!!savedEmail)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { email: savedEmail, password: '' },
  })

  const [challenge, setChallenge] = useState<MfaChallenge | null>(null)

  const navigateAfterLogin = () => {
    const { user, tenant } = useAuthStore.getState()
    if (user?.role !== 'super_admin' && tenant?.id) {
      navigate(`/${tenant.id}/dashboard`)
    } else {
      navigate('/dashboard')
    }
  }

  const onSubmit = async (formData: LoginFormData) => {
    setError(null)
    try {
      if (rememberMe) {
        safeStorage(() => localStorage.setItem(REMEMBERED_EMAIL_KEY, formData.email))
      } else {
        safeStorage(() => localStorage.removeItem(REMEMBERED_EMAIL_KEY))
      }
      const mfa = await login(formData.email, formData.password, rememberMe)
      if (mfa) {
        setChallenge(mfa)
        return
      }
      navigateAfterLogin()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    }
  }

  if (challenge) {
    return (
      <MfaStep
        challenge={challenge}
        rememberMe={rememberMe}
        onDone={navigateAfterLogin}
        onBack={() => setChallenge(null)}
      />
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-linear-to-br from-teal-50 to-amber-50/30 p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <img src="/fdclogo-banner.png" alt="FrontDeskCollect.ai" className="h-10 w-auto" />
          </div>
          <CardTitle className="text-xl md:text-2xl">Welcome Back</CardTitle>
          <CardDescription>
            Sign in to manage your collections with ease
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {error && (
              <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@company.com"
                {...register('email', EMAIL_RULES)}
                className={errors.email ? 'border-destructive' : ''}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  {...register('password', PASSWORD_RULES)}
                  className={errors.password ? 'border-destructive pr-10' : 'pr-10'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v: boolean) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 z-10 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="remember-me"
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked === true)}
                  aria-label="Remember Me"
                  className="cursor-pointer"
                />
                <Label htmlFor="remember-me" className="text-sm font-normal cursor-pointer">
                  Remember Me
                </Label>
              </div>
              <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                Forgot Password?
              </Link>
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </Button>

          </form>
        </CardContent>
      </Card>
    </div>
  )
}
